import { useMemo } from "react";
import { format, parseISO, isValid } from "date-fns";
import {
  MapPin,
  Calendar as CalIcon,
  Plane,
  Users,
  Briefcase,
  Car,
  Pencil,
} from "lucide-react";

import { cn } from "@/lib/utils";
import QuoteSummary from "@/components/QuoteSummary";
import { formatTimeLabel } from "@/components/TimeSelect";

/**
 * Premium review-before-payment screen (Step 3 of the booking wizard).
 * Shows an at-a-glance itinerary + prominent fare, with Edit buttons that
 * jump the wizard back to the relevant step.
 */
export default function ReviewItinerary({
  serviceType,
  transferType,
  details,
  vehicleName,
  quotePayload,
  onEditTrip,
  onEditVehicle,
}) {
  const isAirport = serviceType === "Airport Transfers";
  const isReturn = transferType === "Return (Both)";

  // --- pickup / destination / date / time resolved from the details schema ---
  const journey = useMemo(() => {
    // Return-Both is two legs; show both.
    if (isAirport && isReturn) {
      return {
        legs: [
          {
            label: "Arrival",
            from: details.arrival_airport || "—",
            to: details.arrival_dropoff_address || details.dropoff_address || details.pickup_address || "—",
            date: details.arrival_date,
            time: details.arrival_pickup_time || details.arrival_time,
          },
          {
            label: "Departure",
            from: details.pickup_address || "—",
            to: details.departure_airport || "—",
            date: details.departure_date,
            time: details.departure_pickup_time || details.departure_time,
          },
        ],
      };
    }

    const pickup =
      details.pickup_location ||
      details.pickup_address ||
      details.arrival_airport ||
      "—";
    const destination =
      details.destination ||
      details.dropoff_address ||
      details.destination_airport ||
      details.wedding_venue ||
      details.event_location ||
      details.cruise_terminal ||
      "—";
    const date =
      details.pickup_date ||
      details.arrival_date ||
      details.departure_date ||
      details.cruise_date ||
      details.ceremony_date ||
      details.event_date;
    const time =
      details.pickup_time ||
      details.arrival_time ||
      details.departure_time ||
      details.cruise_time ||
      details.ceremony_time ||
      details.event_start_time;
    return { pickup, destination, date, time };
  }, [details, isAirport, isReturn]);

  // --- flight info ---
  const flightInfo = useMemo(() => {
    if (!isAirport) return null;
    if (isReturn) {
      return {
        arrival: {
          number: details.arrival_flight_number,
          airline: details.arrival_airline_auto || details.arrival_airline,
        },
        return: {
          number: details.return_flight_number,
          airline: details.return_airline_auto || details.return_airline,
        },
        flightType: details.flight_type_auto || details.flight_type,
      };
    }
    return {
      number: details.flight_number,
      airline: details.airline_auto || details.airline,
      flightType: details.flight_type_auto || details.flight_type,
    };
  }, [details, isAirport, isReturn]);

  const passengers = details.passengers ?? "—";
  const luggage = details.luggage_count ?? "—";
  const oversized = details.oversized_luggage;

  return (
    <div data-testid="review-itinerary" className="space-y-6">
      <div>
        <p className="sjc-eyebrow text-muted-light flex items-center gap-3">
          <span className="sjc-hairline w-6" />
          Step 3 of 4
        </p>
        <h2 className="font-display mt-4 text-3xl md:text-4xl lg:text-[46px] leading-[1.1] tracking-[-0.015em] text-charcoal">
          Review <span className="italic font-light">your journey.</span>
        </h2>
        <p className="mt-3 text-sm md:text-base text-muted-light font-light">
          Please confirm the details before we take you to secure payment.
        </p>
      </div>

      {/* Journey block */}
      <ReviewCard
        icon={MapPin}
        title="Journey"
        onEdit={onEditTrip}
        testid="review-journey"
      >
        {journey.legs ? (
          <div className="space-y-4">
            {journey.legs.map((leg, i) => (
              <div key={i} className="relative pl-6">
                <span className="absolute left-0 top-0.5 text-[10px] font-semibold tracking-[0.22em] uppercase sjc-gold">
                  {leg.label}
                </span>
                <div className="pt-6 space-y-2">
                  <RowLine from={leg.from} to={leg.to} />
                  {(leg.date || leg.time) && (
                    <p className="text-xs text-zinc-500">
                      {formatDate(leg.date)}{leg.time ? ` · ${formatTimeLabel(leg.time) || leg.time}` : ""}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            <RowLine from={journey.pickup} to={journey.destination} />
            {(journey.date || journey.time) && (
              <p className="text-xs text-zinc-500">
                {formatDate(journey.date)}
                {journey.time ? ` · ${formatTimeLabel(journey.time) || journey.time}` : ""}
              </p>
            )}
          </div>
        )}
      </ReviewCard>

      {/* Flight block (airport only) */}
      {flightInfo && (
        <ReviewCard
          icon={Plane}
          title="Flight"
          onEdit={onEditTrip}
          testid="review-flight"
        >
          {flightInfo.arrival ? (
            <div className="space-y-2 text-sm text-zinc-700">
              <FlightLine label="Arrival" number={flightInfo.arrival.number} airline={flightInfo.arrival.airline} />
              <FlightLine label="Return" number={flightInfo.return.number} airline={flightInfo.return.airline} />
              {flightInfo.flightType && (
                <FlightBadge type={flightInfo.flightType} />
              )}
            </div>
          ) : (
            <div className="space-y-2 text-sm text-zinc-700">
              <div className="flex items-baseline gap-2">
                <span className="text-zinc-500 text-xs w-16">Flight</span>
                <span className="font-medium">{flightInfo.number || "—"}</span>
                {flightInfo.airline && (
                  <span className="text-zinc-500">· {flightInfo.airline}</span>
                )}
              </div>
              {flightInfo.flightType && <FlightBadge type={flightInfo.flightType} />}
            </div>
          )}
        </ReviewCard>
      )}

      {/* Passengers + Luggage */}
      <ReviewCard
        icon={Users}
        title="Passengers &amp; Luggage"
        onEdit={onEditTrip}
        testid="review-passengers"
      >
        <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
          <MetaKV label="Passengers" value={passengers} />
          <MetaKV label="Luggage" value={luggage} />
          {oversized === "Yes" && (
            <MetaKV label="Oversized" value="Yes" highlight />
          )}
        </div>
      </ReviewCard>

      {/* Vehicle */}
      <ReviewCard
        icon={Car}
        title="Vehicle"
        onEdit={onEditVehicle}
        testid="review-vehicle"
      >
        <p className="text-sm text-zinc-800 font-medium">{vehicleName || "—"}</p>
      </ReviewCard>

      {/* Fare */}
      <div data-testid="review-fare">
        <QuoteSummary payload={quotePayload} title="Fare" defaultOpen />
      </div>
    </div>
  );
}

/* ---------- helpers ---------- */

function ReviewCard({ icon: Icon, title, children, onEdit, testid }) {
  return (
    <div
      data-testid={testid}
      className="border border-charcoal/10 bg-white p-6 md:p-7"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <span className="w-9 h-9 border border-charcoal/15 grid place-items-center text-gold">
            <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
          </span>
          <h3
            className="font-display text-[19px] md:text-[21px] text-charcoal leading-tight tracking-tight"
            dangerouslySetInnerHTML={{ __html: title }}
          />
        </div>
        {onEdit && (
          <button
            type="button"
            data-testid={`${testid}-edit`}
            onClick={onEdit}
            className="group text-[10px] font-medium tracking-[0.24em] uppercase text-muted-light hover:text-charcoal transition-colors duration-300 inline-flex items-center gap-2"
          >
            <Pencil className="w-3 h-3" strokeWidth={1.75} />
            <span>Edit</span>
          </button>
        )}
      </div>
      <div className="mt-6">{children}</div>
    </div>
  );
}

function RowLine({ from, to }) {
  return (
    <div className="relative pl-6">
      <div className="absolute left-[7px] top-2 bottom-2 w-px bg-charcoal/15" aria-hidden />
      <div className="flex items-start gap-3">
        <span className="w-3.5 h-3.5 rounded-full border-2 border-gold mt-0.5 flex-shrink-0" />
        <p className="text-[15px] text-charcoal break-words -ml-6 pl-6 font-light">{from}</p>
      </div>
      <div className="flex items-start gap-3 mt-3">
        <span className="w-3.5 h-3.5 rounded-full bg-gold mt-0.5 flex-shrink-0" />
        <p className="text-[15px] text-charcoal break-words -ml-6 pl-6 font-light">{to}</p>
      </div>
    </div>
  );
}

function FlightLine({ label, number, airline }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="text-muted-light text-[10px] tracking-[0.22em] uppercase w-16 font-medium">{label}</span>
      <span className="font-medium text-charcoal">{number || "—"}</span>
      {airline && <span className="text-muted-light">· {airline}</span>}
    </div>
  );
}

function FlightBadge({ type }) {
  return (
    <span
      data-testid="review-flight-type"
      className="inline-flex items-center gap-2 bg-ivory border border-gold/25 px-3 py-1.5 text-[11px] font-medium text-charcoal"
    >
      <span className="w-1.5 h-1.5 rounded-full bg-gold" />
      {type} Flight
    </span>
  );
}

function MetaKV({ label, value, highlight = false }) {
  return (
    <div>
      <p className="text-[10px] font-medium tracking-[0.22em] uppercase text-muted-light">
        {label}
      </p>
      <p
        className={cn(
          "mt-1 text-[15px] font-medium",
          highlight ? "text-gold" : "text-charcoal",
        )}
      >
        {value}
      </p>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = parseISO(iso);
  return isValid(d) ? format(d, "EEE, d MMM yyyy") : iso;
}
